import api from '@/api'
import Vue from 'vue'
import i18n from '@/plugins/i18n.plugin'

import { HTTP_STATUS, SIM_ESTADOS_VISUALIZACION } from '@/helpers/constants'
import {
  defineSyncSchema,
  createSyncState,
  recordWrite,
  createSyncRequest,
  isStaleResponse,
  collectProtected,
  mergeRows,
  schemaKinds,
  syncMutations
} from '@/helpers/storeSync'

const SIMULACION_SYNC_SCHEMA = defineSyncSchema({ key: 'ID_SIMULACION', kind: 'simulacion', insertAt: 'end' })

const state = {
  simulaciones: [],
  simulacionesWind: [],
  sync: createSyncState(schemaKinds(SIMULACION_SYNC_SCHEMA))
}

const getters = {
  simulaciones: (state) => state.simulaciones,
  simulacionesWind: (state) => state.simulacionesWind,
  getSimulacionByIncendioID: (state) => (idIncendio) => {
    return state.simulaciones.filter(x => x.ID_INCENDIO === idIncendio)
  }
}

const mutations = {
  ...syncMutations,

  MERGE_SIMULACIONES(state, { incendio, viento, request }) {
    if (isStaleResponse(state.sync, request)) return

    const protectedByKind = collectProtected(state.sync.stamps, request)
    state.simulaciones = mergeRows(state.simulaciones, incendio, SIMULACION_SYNC_SCHEMA, protectedByKind)
    state.simulacionesWind = mergeRows(state.simulacionesWind, viento, SIMULACION_SYNC_SCHEMA, protectedByKind)
    state.sync.appliedSeq = request.seq
  },

  ADD_SIMULACION(state, simulacion) {
    state.simulaciones.push(simulacion)
    recordWrite(state.sync, 'simulacion', simulacion.ID_SIMULACION)
  },

  UPDATE_SIMULACION(state, simulacion) {
    const index = state.simulaciones.findIndex(item => item.ID_SIMULACION === simulacion.ID_SIMULACION)
    if (index !== -1) {
      state.simulaciones[index] = simulacion
      recordWrite(state.sync, 'simulacion', simulacion.ID_SIMULACION)
    }
  },

  DELETE_SIMULACION(state, simulacion) {
    const idx = state.simulaciones.findIndex(
      a => a.ID_SIMULACION === simulacion.ID_SIMULACION
    )
    if (idx !== -1) state.simulaciones.splice(idx, 1)
    recordWrite(state.sync, 'simulacion', simulacion.ID_SIMULACION, true)
  },

  SOCKET_SET_SIMULACION_ESTADO_BY_ID(state, data) {
    const estados = data?.ESTADOS
    if (!estados) {
      return
    }

    const idSimulacion = data.idSimulacion
    const index = state.simulaciones.findIndex(item => item.ID_SIMULACION.equalsIgnoreCase(idSimulacion))
    const indexViento = state.simulacionesWind.findIndex(item => item.ID_SIMULACION.equalsIgnoreCase(idSimulacion))

    const estadoAnterior = index !== -1
      ? state.simulaciones[index].ESTADOS?.SIMULACION
      : indexViento !== -1
        ? state.simulacionesWind[indexViento].ESTADOS?.SIMULACION
        : null

    if (index !== -1) {
      Vue.set(state.simulaciones, index, { ...state.simulaciones[index], ESTADOS: estados })
    }

    if (indexViento !== -1) {
      Vue.set(state.simulacionesWind, indexViento, { ...state.simulacionesWind[indexViento], ESTADOS: estados })
    }

    if (index !== -1 || indexViento !== -1) {
      recordWrite(state.sync, 'simulacion', idSimulacion)
    }

    const mensajeKey = SIM_ESTADOS_VISUALIZACION[estados.SIMULACION]
    const esTransicionNueva = estados.SIMULACION !== estadoAnterior

    if ((index !== -1 || indexViento !== -1) && mensajeKey && esTransicionNueva) {
      this.notify(i18n.t('simulator.notifyTitle'), i18n.t(mensajeKey))
    }
  }
}

const actions = {
  async fetchSimulaciones({ commit, state }, data) {

    try {
      commit('BEGIN_SYNC_REQUEST')
      const request = createSyncRequest(state.sync.seq)
      const response = await api.simulacion.getSimulaciones(data)
      const todasLasSimulaciones = response.data

      const simulacionesIncendio = todasLasSimulaciones.filter(s => s.TIPO === 'INCENDIO')
      const simulacionesViento = todasLasSimulaciones.filter(s => s.TIPO === 'VIENTO')

      commit('MERGE_SIMULACIONES', { incendio: simulacionesIncendio, viento: simulacionesViento, request })

    } catch (err) {
      console.error(err)
      throw err
    }
  },

  async addSimulacion({ commit }, data) {

    const datosSimulador = data.datosSimulador
    const datosBackend = data.datosBackend

    try {
      let responseSimulador = null
      const callbackUrl = await api.simulacion.generateCallback()
      datosSimulador.simulationReportStatusURL = callbackUrl.data

      if (data.tipo === 'INCENDIO') {
        responseSimulador = await api.simulacion.simulate(datosSimulador) // Crear simulacion en server simulacion
      } else {
        responseSimulador = await api.simulacion.simulateWind(datosSimulador) // Crear simulacion de vientos en server simulacion
      }

      if (responseSimulador.status === HTTP_STATUS.ACCEPTED) {
        datosBackend.ID_SIMULACION = responseSimulador.data
        datosBackend.TIPO = data.tipo
        const responseBackend = await api.simulacion.addSimulacion(datosBackend) // Insertar simulacion en BBDD backend

        if (responseBackend.status === HTTP_STATUS.OK) {
          const responseEstado = await api.simulacion.setStatusSimulationRepo(responseSimulador.data, 'NEW')

          if (responseEstado.status === HTTP_STATUS.OK) {
            commit('ADD_SIMULACION', responseBackend.data)
          }
        }
      }
    } catch (err) {
      console.error(err)
      throw err
    }
  },

  async deleteSimulacion({ commit }, simulacion) {
    try {
      const response = await api.simulacion.deleteSimulacionBDD(simulacion.ID_SIMULACION)

      if (response.status === HTTP_STATUS.OK) {
        commit('DELETE_SIMULACION', simulacion)
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  },

  async exportSimulacion(_ctx, { idSimulacion, tipo, fechas, responseType, fileType }) {
    try {
      const res = await api.simulacion.exportSimulacion(idSimulacion, tipo, JSON.stringify(fechas), responseType, fileType)
      return res
    } catch (err) {
      if (err?.response) return err.response
      throw err
    }
  },

  async getSimulacionPdf(_, simulacion) {
    try {
      const response = await api.simulacion.getSimulationPDF(simulacion.ID_SIMULACION)
      return response.data
    } catch (error) {
      console.error('Error obteniendo la informacion de la simulacion', error)
      throw error
    }
  }
}

const module = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default module
